import { useForm } from "react-hook-form";
import { Alert, Button, Form } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { useAppContext } from "../../Context/AppContext";
import { useBookEvent } from "../../api/mutations/useBookEvent";
import { useQueryClient } from "@tanstack/react-query";
import {
  lazy,
  memo,
  Suspense,
  useCallback,
  useMemo,
  useState,
} from "react";
import { useGetBookedEvents } from "../../api/queries/useGetBookedEvents";
import { BookedEvents } from "../../types/Types";
import { useGetEvent } from "../../api/queries/useGetEvent";

const EventSuccessModal = lazy(() => import("./EventSuccessModal"));

const EVENT_DATA_KEY = "event-data";
const MEMBERSHIP_EXECUTIVE = "Executive Membership";
const CONFIRM_BOOKING_PATH = "/confirm-booking";

const stageOptions = [
  { value: "Early Stage", label: "Early Stage" },
  { value: "Growth", label: "Growth" },
  { value: "Established", label: "Established" },
  { value: "Executive", label: "Executive" },
];

const objectivesOptions = [
  "B2B Lead generation",
  "Finding reliable B2B services",
  "Getting mentorship/advice from experienced business owners",
  "Supporting growing Muslim businesses",
  "Finding potential investment opportunities",
  "Meeting potential investors",
  "Meeting online connections in person",
  "Learning more about SMBF / SMBF Services",
  "General networking",
  "Exploring business partnerships",
  "Other",
];

const sectorOptions = [
  "Technology & Software",
  "Healthcare & Life Sciences",
  "Financial Services",
  "Retail & E-Commerce",
  "Manufacturing & Industrial",
  "Construction & Real Estate",
  "Professional Services",
  "Hospitality & Tourism",
  "Education & Training",
  "Energy & Utilities",
  "Transportation & Logistics",
  "Agriculture & Food Production",
  "Media & Entertainment",
  "Non-Profit & Social Enterprises",
  "Consumer Services",
  "Other",
];

const SORTED_STAGE_OPTIONS = [...stageOptions].sort((a, b) =>
  a.label.localeCompare(b.label)
);
const SORTED_OBJECTIVES_OPTIONS = [...objectivesOptions].sort();
const SORTED_SECTOR_OPTIONS = [...sectorOptions].sort();

const EXECUTIVE_TOAST_MESSAGE =
  "Your Executive Membership includes one free monthly ticket for this event.";

type FormValues = {
  stageOfBusiness?: string;
  objectives: string;
  sectorsOfInterest?: string[];
  consentToShare: boolean;
};

interface ExecutiveTicketToastProps {
  onClose: () => void;
}

const ExecutiveTicketToast = memo(function ExecutiveTicketToast({
  onClose,
}: ExecutiveTicketToastProps) {
  return (
    <Alert
      variant="success"
      className="mb-4 rounded-4 shadow-lg d-flex flex-column flex-md-row justify-content-between align-items-center gap-2"
    >
      <div className="text-center text-md-left">{EXECUTIVE_TOAST_MESSAGE}</div>
      <button
        type="button"
        className="btn-close"
        aria-label="Close"
        onClick={onClose}
      />
    </Alert>
  );
});
ExecutiveTicketToast.displayName = "ExecutiveTicketToast";

function EventQuestions() {
  const { objectId, user } = useAppContext();
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showTicketToast, setShowTicketToast] = useState(true);
  const { data: bookedEvents } = useGetBookedEvents({ id: objectId || "" });
  const { data } = useGetEvent({ eventId: eventId || "" });
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>();

  const onBookSuccess = useCallback(
    (data: unknown) => {
      queryClient.invalidateQueries({ queryKey: ["booked-events", data] });
      localStorage.removeItem(EVENT_DATA_KEY);
      setShowSuccessModal(true);
    },
    [queryClient]
  );
  const { mutate: bookEvent } = useBookEvent({ onSuccess: onBookSuccess });

  const filteredEvents = useMemo(() => {
    return (bookedEvents as unknown as BookedEvents[])?.filter((event) => {
      if (!event?.eventDate || !data?.eventDate) return false;

      const eventDate = new Date(event.eventDate);
      const targetDate = new Date(data.eventDate);

      const eventMonth = eventDate.getMonth();
      const eventYear = eventDate.getFullYear();
      const targetMonth = targetDate.getMonth();
      const targetYear = targetDate.getFullYear();

      return eventMonth === targetMonth && eventYear === targetYear;
    });
  }, [bookedEvents, data?.eventDate]);

  const isExecutiveMember = useMemo(
    () => user?.user?.membershipName === MEMBERSHIP_EXECUTIVE,
    [user?.user?.membershipName]
  );

  const shouldShowToast = useMemo(
    () =>
      showTicketToast &&
      filteredEvents?.length < 1 &&
      isExecutiveMember,
    [showTicketToast, filteredEvents?.length, isExecutiveMember]
  );

  const onSubmit = useCallback(
    (formData: FormValues) => {
      localStorage.setItem(EVENT_DATA_KEY, JSON.stringify(formData));
      if (filteredEvents.length > 0 || !isExecutiveMember) {
        navigate(`${CONFIRM_BOOKING_PATH}/${eventId}`);
      } else {
        bookEvent({
          stageOfBusiness: formData.stageOfBusiness,
          eventObjectives: formData.objectives,
          sectorsOfInterest: (formData.sectorsOfInterest ?? []).join(", "),
          consentToShareProfile: formData.consentToShare ? 1 : 0,
          eventId: eventId ? parseInt(eventId, 10) : 0,
          userId: objectId || "",
        });
      }
    },
    [
      filteredEvents.length,
      isExecutiveMember,
      navigate,
      eventId,
      bookEvent,
      objectId,
    ]
  );

  const handleCloseToast = useCallback(() => {
    setShowTicketToast(false);
  }, []);

  const handleCloseModal = useCallback(() => {
    setShowSuccessModal(false);
  }, []);

  return (
    <main className="container p-4" aria-label="Event registration questions">
      {shouldShowToast && (
        <ExecutiveTicketToast onClose={handleCloseToast} />
      )}

      <div className="card shadow-lg rounded-4 border-0">
        <Form onSubmit={handleSubmit(onSubmit)} className="p-3">
          <Form.Group controlId="stageOfBusiness" className="mb-3">
            <Form.Label className="fw-semibold">Stage of Business</Form.Label>
            <Form.Select {...register("stageOfBusiness")}>
              <option value="">Select</option>
              {SORTED_STAGE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group controlId="objectives" className="mb-3">
            <Form.Label className="fw-semibold">
              What are your objectives for attending the event? <span>*</span>
            </Form.Label>
            <Form.Select
              {...register("objectives", {
                required: "This field is required",
              })}
            >
              <option value="">Select</option>
              {SORTED_OBJECTIVES_OPTIONS.map((opt: string) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </Form.Select>
            {errors.objectives && (
              <Form.Text className="text-danger">
                {errors.objectives.message}
              </Form.Text>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">Sectors of Interest</Form.Label>
            <div>
              {SORTED_SECTOR_OPTIONS.map((sector, index) => {
                const fieldId = `sector-${index}`;
                return (
                  <Form.Check
                    key={sector}
                    id={fieldId}
                    type="checkbox"
                    value={sector}
                    label={sector}
                    {...register("sectorsOfInterest", {
                      validate: (value) =>
                        (value && value.length > 0) ||
                        "At least one sector must be selected",
                    })}
                  />
                );
              })}
            </div>
            {errors.sectorsOfInterest && (
              <Form.Text className="text-danger">
                {errors.sectorsOfInterest.message}
              </Form.Text>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">
              Do you consent to share your profile and registration info on the
              delegate list? <span>*</span>
            </Form.Label>
            <Form.Check
              id="consentToShare"
              type="checkbox"
              label="Yes, I consent"
              {...register("consentToShare", {
                required: "Consent is required to proceed",
              })}
            />
            {errors.consentToShare && (
              <Form.Text className="text-danger">
                {errors.consentToShare.message}
              </Form.Text>
            )}
          </Form.Group>

          <div className="d-flex justify-content-end mt-5">
            <Button className="px-4" type="submit">
              Submit
            </Button>
          </div>
        </Form>
      </div>
      {showSuccessModal && (
        <Suspense fallback={null}>
          <EventSuccessModal
            show={showSuccessModal}
            onHide={handleCloseModal}
            text="Your booking is confirmed!"
          />
        </Suspense>
      )}
    </main>
  );
}

EventQuestions.displayName = "EventQuestions";

export default memo(EventQuestions);
